//#region ../src/ar/cell-key.ts
/** Full-field envelope: direct cell keys are collision-free for `|c| ≤` this. */
const CELL_KEY_LIMIT = 65535;
/**
* Mesher-tier envelope: leaves headroom for the derived half-lattice
* (`2·coord ± 1`) and neighbour (`±1`) keys the meshers build from a cell.
*/
const HALF_LATTICE_CELL_KEY_LIMIT = 32767;
const KEY_OFFSET = 65536;
const KEY_FIELD = 131072;
const KEY_FIELD_SQ = KEY_FIELD * KEY_FIELD;
/**
* Pack three integer coordinates into the numeric key. Collision-free only
* within the caller's envelope (see the module header); scalar form so hot
* loops need no tuple.
*/
function packCellKey(x, y, z) {
	return (x + KEY_OFFSET) * KEY_FIELD_SQ + (y + KEY_OFFSET) * KEY_FIELD + (z + KEY_OFFSET);
}
/** Tuple convenience over {@link packCellKey}. */
function cellKey(cell) {
	return packCellKey(cell[0], cell[1], cell[2]);
}
/** One axis of {@link unpackCellKey} without allocating the tuple (hot paths). */
function unpackCellCoord(key, axis) {
	if (axis === 0) return Math.floor(key / KEY_FIELD_SQ) - KEY_OFFSET;
	if (axis === 1) return Math.floor(key / KEY_FIELD) % KEY_FIELD - KEY_OFFSET;
	return key % KEY_FIELD - KEY_OFFSET;
}
/**
* Exact inverse of {@link cellKey} over the ±{@link CELL_KEY_LIMIT} envelope
* (property-tested). Consumers that no longer store coordinate tuples recover
* them from the key through this function.
*/
function unpackCellKey(key) {
	return [
		unpackCellCoord(key, 0),
		unpackCellCoord(key, 1),
		unpackCellCoord(key, 2)
	];
}
/**
* Per-axis key strides: stepping one cell along an axis moves the packed key
* by a constant (positional base-2^17 packing) — `packCellKey(x+1, y, z) ===
* packCellKey(x, y, z) + CELL_KEY_STRIDE_X`, and the z stride is 1. Hot walks
* (the occupancy grid's fused carve, the smooth mesher's dual-cell corners)
* maintain keys incrementally with these instead of repacking per cell.
* DERIVED from the packer itself so they can never drift from the encoding.
*/
const CELL_KEY_STRIDE_X = packCellKey(1, 0, 0) - packCellKey(0, 0, 0);
/** See {@link CELL_KEY_STRIDE_X}. */
const CELL_KEY_STRIDE_Y = packCellKey(0, 1, 0) - packCellKey(0, 0, 0);
/**
* True iff every coordinate is within ±`limit` — the aliasing guard callers
* must apply before keying untrusted coordinates. Defaults to the full-field
* {@link CELL_KEY_LIMIT}; the meshers pass {@link HALF_LATTICE_CELL_KEY_LIMIT}.
*/
function cellCoordsInKeyRange(cell, limit = CELL_KEY_LIMIT) {
	return Math.abs(cell[0]) <= limit && Math.abs(cell[1]) <= limit && Math.abs(cell[2]) <= limit;
}
//#endregion
export { cellCoordsInKeyRange as a, unpackCellCoord as c, HALF_LATTICE_CELL_KEY_LIMIT as i, unpackCellKey as l, CELL_KEY_STRIDE_X as n, cellKey as o, CELL_KEY_STRIDE_Y as r, packCellKey as s, CELL_KEY_LIMIT as t };
